import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { RoleType } from '../../models/Role'
import { type InviteResponse, useResendInviteMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { readApiMessage } from './apiMessage'
import { buySeatStepModalProps } from './buySeatStepModal'
import { getRoleGroupForRoles } from './InviteRoleGroups'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { useSeatPurchaseGate } from './seatPurchaseGate'
import { inviteName } from './teamRows'
import { useAssistantSeatsOffered } from './useAssistantSeatsOffered'
import { useInviteFlowRevampEnabled } from './useInviteFlowRevampEnabled'
import { useBuySeat } from './useBuySeat'
import { useRefreshSeats } from './useRefreshSeats'

const RESEND_FAILED = 'The invitation could not be resent. Please try again.'

const SEAT_REQUESTED = 'Seat was requested for this invite'

interface ResendInviteModalProps {
  invite?: InviteResponse
  show: boolean
  onHide: () => void
}

interface PurchaseTarget {
  invitationId: string
  seatType: RoleType
}

export const ResendInviteModal: React.FC<ResendInviteModalProps> = ({ invite, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [resendInvite, resendInviteResult] = useResendInviteMutation()
  const { isEnabled: revampEnabled } = useInviteFlowRevampEnabled()
  const canBuySeats = useSeatPurchaseGate()
  const { offered: assistantOffered } = useAssistantSeatsOffered()
  const refreshSeats = useRefreshSeats()
  const [resendError, setResendError] = useState<string | null>(null)
  const [purchaseTarget, setPurchaseTarget] = useState<PurchaseTarget | null>(null)
  const buySeat = useBuySeat(purchaseTarget && invite ? { ...purchaseTarget, email: invite.user.email } : null, {
    onSeatsChanged: refreshSeats,
    onSent: onHide,
    onGone: onHide,
  })

  type FormValues = {
    ccMe: boolean
  }

  const defaultValues = {
    ccMe: false,
  }

  const { control, handleSubmit } = useForm<FormValues>({ defaultValues })

  useEffect(() => {
    if (resendInviteResult.isSuccess && !revampEnabled) {
      onHide()
    }
  }, [resendInviteResult.isSuccess, revampEnabled, onHide])

  if (!invite) {
    return null
  }

  const seatType = getRoleGroupForRoles(invite.user.roleIds)?.type
  const offersPurchase = canBuySeats && !!seatType && (seatType !== RoleType.SIDECAR || assistantOffered)

  const closeAsRequested = () => {
    dispatch(addToast({ variant: 'success', title: SEAT_REQUESTED }))
    onHide()
  }

  const onSubmit = async (data: FormValues) => {
    const request = { accountId, inviteId: invite.id, body: { ccMe: data.ccMe } }

    if (!revampEnabled) {
      resendInvite(request)
      return
    }

    setResendError(null)
    try {
      const { state, invitationId } = await resendInvite(request).unwrap()
      refreshSeats()
      if (state !== 'requested') {
        onHide()
        return
      }
      if (offersPurchase && seatType) {
        setPurchaseTarget({ invitationId, seatType })
        return
      }
      closeAsRequested()
    } catch (error) {
      setResendError(readApiMessage(error, RESEND_FAILED))
    }
  }

  const body = (
    <Container>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            Are you sure you want to resend invitation for email <b>{invite.user.email}</b> ?
          </div>
          <form className='d-flex gap-2'>
            <Controller
              name='ccMe'
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Form.Check type='checkbox' checked={field.value === true} onChange={field.onChange} label='Send invitation copy to me' />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </form>
          {resendError ? <FormFeedback>{resendError}</FormFeedback> : null}
        </Col>
      </Row>
    </Container>
  )
  const stepProps = purchaseTarget
    ? buySeatStepModalProps({
        firstLine: `You're about to buy 1 ${seatTypeLabels[purchaseTarget.seatType]} seat for ${inviteName(invite.user)}.`,
        secondLine: "Once you confirm, we'll send their invitation. Not ready? Cancel and the user stays in the requested list.",
        buy: buySeat,
        onDismiss: closeAsRequested,
      })
    : {
        header: <h4>Resend Invitation</h4>,
        body,
        footer: undefined,
        showFooterActions: true,
        onFooterPrimaryAction: handleSubmit(onSubmit),
        footerPrimaryActionTitle: 'Resend',
        footerPrimaryActionLoading: resendInviteResult.isLoading,
        footerPrimaryActionDisabled: false,
        footerSecondaryActionDisabled: false,
        onFooterSecondaryAction: onHide,
        onHide,
      }

  return <Modal show={show} headerBodySeparator {...stepProps} />
}
